import { useState } from 'react';
import { useNavigate, useLocation, useSearchParams, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';

const ERROR_MESSAGES = {
  invalid_credentials: 'That email and password didn’t match.',
  no_organization: 'Your account exists but isn’t a member of any organization yet.',
  email_and_password_required: 'Please enter both email and password.',
  invalid_token: 'Your session expired. Please sign in again.',
};

export default function Login() {
  const { login, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Preserve both pathname AND search so deep links like
  // /account/billing?plan=pro&cadence=monthly survive the login round-trip.
  const fromState = location.state?.from;
  const from = fromState
    ? `${fromState.pathname || '/dashboard'}${fromState.search || ''}`
    : '/dashboard';
  const justReset = searchParams.get('reset') === '1';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);

  if (isAuthenticated) {
    return <Navigate to={from} replace />;  // `from` already includes search params
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(ERROR_MESSAGES[err.message] || ERROR_MESSAGES[err.body?.error] || 'Sign-in failed. Try again.');
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <header className="login-header">
          <div className="brand-mark">
            <img src="/icons/markaii-m.png?v=2" alt="" className="brand-mark-icon" />
            <span>Markaii</span>
          </div>
          <h1>Sign in</h1>
          <p className="login-sub">Multi-brand AI marketing</p>
        </header>

        <form onSubmit={handleSubmit} className="login-form" autoComplete="on">
          <label className="login-field">
            <span>Email</span>
            <input
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={loading}
              autoFocus
            />
          </label>

          <label className="login-field">
            <span>Password</span>
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              disabled={loading}
            />
          </label>

          {justReset && (
            <div className="login-success">Password updated — sign in with your new password.</div>
          )}
          {error && <div className="login-error">{error}</div>}

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>

          <p className="login-footnote">
            New here? <Link to="/signup">Create an account</Link>
            <br />
            Forgot your password? <Link to="/forgot-password">Reset it</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
